// admin/AdminLogin.tsx — passkey-only sign-in for the admin portal.
//
// Mirrors the player surface's "Sign in with passkey" landing-button flow
// (web/src/components/Login.tsx → handlePasskeyLogin) but does NOT expose the
// email-code option. Admins must be enrolled by the player surface first
// (passkey already registered to their existing player account) and then
// granted is_admin via `npm run admin`. There is no account creation path
// here. Players who arrive accidentally get a pointer to www.emberhollow.ai.

import { useEffect, useState } from "react";
import { styles } from "./styles";
import {
  isWebAuthnSupported,
  hasPlatformAuthenticator,
  authenticateWithPasskey,
} from "../services/passkey";
import {
  passkeyAuthenticateOptions,
  passkeyVerifyAuthenticate,
  commitAuth,
} from "../services/authApi";
import type { AuthUser } from "../auth/tokens";

interface AdminLoginProps {
  onSignedIn: (user: AuthUser) => void;
}

export default function AdminLogin({ onSignedIn }: AdminLoginProps) {
  const [supported, setSupported] = useState(false);
  const [hasPlatform, setHasPlatform] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = isWebAuthnSupported();
      if (cancelled) return;
      setSupported(ok);
      if (ok) {
        const plat = await hasPlatformAuthenticator();
        if (!cancelled) setHasPlatform(plat);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handlePasskey() {
    setError(null);
    setBusy(true);
    try {
      const { challengeId, options } = await passkeyAuthenticateOptions();
      const credential = await authenticateWithPasskey(options);
      const result = await passkeyVerifyAuthenticate(challengeId, credential);
      commitAuth(result.token, result.refreshToken, result.user);
      onSignedIn(result.user);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === "NO_PASSKEY_FOUND") {
        setError(
          "No passkey found on this device. Admin staff: enrol a passkey via the player surface first.",
        );
      } else {
        setError("Passkey sign-in did not complete.");
      }
    } finally {
      setBusy(false);
    }
  }

  const disabled = busy || !supported || !hasPlatform;

  return (
    <div style={styles.loginShell}>
      <div style={styles.loginCard}>
        <h1 style={styles.loginTitle}>Ember Hollow — Admin</h1>
        <p style={styles.loginSub}>Sign in with your registered passkey.</p>

        <button
          type="button"
          onClick={handlePasskey}
          disabled={disabled}
          style={{
            ...styles.loginButton,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Signing in…" : "Sign in with passkey"}
        </button>

        {error ? <div style={styles.loginError}>{error}</div> : null}

        {!supported ? (
          <div style={styles.loginHint}>
            This browser doesn&apos;t support passkeys. Use a passkey-capable
            browser to access the admin portal.
          </div>
        ) : !hasPlatform ? (
          <div style={styles.loginHint}>
            No platform authenticator detected on this device (Touch ID / Face ID /
            Windows Hello). Sign in from a device that has one.
          </div>
        ) : null}

        <div style={styles.loginHint}>
          Admin staff only.{" "}
          <a
            style={styles.loginLink}
            href="https://www.emberhollow.ai"
            rel="noreferrer noopener"
          >
            Players sign in at www.emberhollow.ai
          </a>
          .
        </div>
      </div>
    </div>
  );
}
